import { useEffect, useState, useCallback, useRef } from "react";

import {
    FaRobot,
    FaSyncAlt,
    FaBatteryFull,
    FaBolt,
    FaExclamationTriangle,
    FaTimes,
    FaMapMarkerAlt,
    FaChargingStation,
    FaWalking,
    FaCheckCircle,
    FaBan,
    FaHourglassHalf,
    FaQuestionCircle
} from "react-icons/fa";

import "./RobotFleet.css";

import { getMenuPermissions } from "../utils/auth";

// API_URL is the API base, e.g. http://172.20.10.2:8000/api
const API_URL = process.env.REACT_APP_API_URL;

const FLEET_ENDPOINT = `${API_URL}/portal/robots/fleet`;
const SUMMARY_ENDPOINT = `${API_URL}/portal/robots/fleet/summary`;
const SYNC_ENDPOINT = `${API_URL}/portal/robots/fleet/sync`;

const AUTO_REFRESH_MS = 30000; // refresh fleet table every 30 seconds

function dailyReportEndpoint(robotSn, dateStr) {

    return `${API_URL}/portal/robots/${encodeURIComponent(robotSn)}/daily-report?report_date=${dateStr}`;

}

function commandHistoryEndpoint(robotSn) {

    return `${API_URL}/portal/robots/${encodeURIComponent(robotSn)}/commands`;

}

function goChargingEndpoint(robotSn) {

    return `${API_URL}/portal/robots/${encodeURIComponent(robotSn)}/commands/go-charging`;

}

function navigateEndpoint(robotSn) {

    return `${API_URL}/portal/robots/${encodeURIComponent(robotSn)}/commands/navigate`;

}

function formatDateTime(value) {

    if (!value) {
        return "-";
    }

    return new Date(value).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });

}

function formatTime(value) {

    if (!value) {
        return "-";
    }

    return new Date(value).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });

}

function formatDuration(seconds) {

    if (seconds === null || seconds === undefined) {
        return "ongoing";
    }

    const m = Math.floor(seconds / 60);

    const h = Math.floor(m / 60);

    if (h > 0) {
        return `${h}h ${m % 60}m`;
    }

    return `${m}m`;

}

function getTodayDateString() {

    const now = new Date();

    const year = now.getFullYear();

    const month = String(now.getMonth() + 1).padStart(2, "0");

    const day = String(now.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;

}

function batteryClass(pct) {

    if (pct === null || pct === undefined) {
        return "mid";
    }

    if (pct <= 20) {
        return "low";
    }

    if (pct <= 50) {
        return "mid";
    }

    return "ok";

}

// Outcome is computed server-side by comparing what the robot's own
// telemetry actually did after this command was sent - see
// robot_service.compute_command_outcome. A "sent" status only means
// Orion's cloud accepted the command; this is what actually tells you
// whether the robot responded.
function OutcomeBadge({ outcome }) {

    if (!outcome || outcome === "not_applicable") {
        return null;
    }

    const config = {

        confirmed: { icon: <FaCheckCircle />, label: "Robot responded", cls: "confirmed" },

        pending: { icon: <FaHourglassHalf />, label: "Waiting to confirm...", cls: "pending" },

        no_response: { icon: <FaQuestionCircle />, label: "No response from robot", cls: "no-response" }

    }[outcome];

    if (!config) {
        return null;
    }

    return (

        <span className={`outcome-badge ${config.cls}`} title="Based on the robot's own telemetry after this command, not just whether Orion accepted it">
            {config.icon} {config.label}
        </span>

    );

}

function StatusPill({ robot }) {

    if (robot.is_emergency_stop) {

        return (
            <span className="pill pill-emergency">
                <FaExclamationTriangle /> Emergency Stop
            </span>
        );

    }

    if (!robot.online) {

        return <span className="pill pill-offline">Offline</span>;

    }

    if (robot.is_charging) {

        return (
            <span className="pill pill-charging">
                <FaBolt /> Charging
            </span>
        );

    }

    if (robot.is_idle) {

        return <span className="pill pill-idle">Idle</span>;

    }

    return (
        <span className="pill pill-busy">
            {robot.task_label || "Active"}
        </span>
    );

}

function BatteryBar({ pct }) {

    const cls = batteryClass(pct);

    return (

        <div className="battery-cell">

            <FaBatteryFull style={{ color: cls === "low" ? "#ef4444" : cls === "mid" ? "#f59e0b" : "#22c55e" }} />

            <div className="battery-track">
                <div
                    className={`battery-fill ${cls}`}
                    style={{ width: `${pct ?? 0}%` }}
                />
            </div>

            <span className="battery-pct">
                {pct === null || pct === undefined ? "-" : `${pct}%`}
            </span>

        </div>

    );

}

// ==============================================================
// Detail Drawer - daily report for a single robot
// ==============================================================

function RobotDrawer({ robot, onClose, onCommandSent }) {

    // Send-to-charging/reception are POST calls, gated by
    // require_menu_permission("robot_fleet") -> "add" on the backend.
    const perms = getMenuPermissions("robot_fleet");

    const [reportDate, setReportDate] = useState(getTodayDateString());

    const [report, setReport] = useState(null);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState(null);

    const [commands, setCommands] = useState([]);

    const [sendingCharge, setSendingCharge] = useState(false);

    const [sendingNav, setSendingNav] = useState(false);

    const [commandNote, setCommandNote] = useState(null);

    const loadReport = useCallback(async () => {

        setLoading(true);
        setError(null);

        try {

            const res = await fetch(dailyReportEndpoint(robot.robot_sn, reportDate));

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            setReport(data);

        } catch (err) {

            console.error("Daily report load failed:", err);

            setError(err.message || "Unable to load daily report");

            setReport(null);

        } finally {

            setLoading(false);

        }

    }, [robot.robot_sn, reportDate]);

    const loadCommands = useCallback(async () => {

        try {

            const res = await fetch(commandHistoryEndpoint(robot.robot_sn));

            if (res.ok) {

                setCommands(await res.json());

            }

        } catch (err) {

            console.error("Command history load failed:", err);

        }

    }, [robot.robot_sn]);

    useEffect(() => {

        loadReport();

    }, [loadReport]);

    useEffect(() => {

        loadCommands();

    }, [loadCommands]);

    const handleSendToCharging = useCallback(async () => {

        setSendingCharge(true);
        setCommandNote(null);

        try {

            const res = await fetch(goChargingEndpoint(robot.robot_sn), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ requested_by: "admin-ui" })
            });

            if (!res.ok) {

                const body = await res.json().catch(() => ({}));

                throw new Error(body.detail || `Request failed with status ${res.status}`);

            }

            setCommandNote({ ok: true, text: "Robot instructed to head to its charging dock." });

            await loadCommands();

            if (onCommandSent) {
                onCommandSent();
            }

        } catch (err) {

            console.error("Send to charging failed:", err);

            setCommandNote({ ok: false, text: err.message || "Failed to send command." });

        } finally {

            setSendingCharge(false);

        }

    }, [robot.robot_sn, loadCommands, onCommandSent]);

    const handleSendToReception = useCallback(async () => {

        setSendingNav(true);
        setCommandNote(null);

        try {

            const res = await fetch(navigateEndpoint(robot.robot_sn), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ requested_by: "admin-ui" })
            });

            if (!res.ok) {

                const body = await res.json().catch(() => ({}));

                throw new Error(body.detail || `Request failed with status ${res.status}`);

            }

            const data = await res.json();

            setCommandNote({ ok: true, text: `Robot instructed to head to ${data.target_pos_name}.` });

            await loadCommands();

            if (onCommandSent) {
                onCommandSent();
            }

        } catch (err) {

            console.error("Send to reception failed:", err);

            setCommandNote({ ok: false, text: err.message || "Failed to send command." });

        } finally {

            setSendingNav(false);

        }

    }, [robot.robot_sn, loadCommands, onCommandSent]);

    return (

        <div className="robot-drawer-backdrop" onClick={onClose}>

            <div className="robot-drawer" onClick={(e) => e.stopPropagation()}>

                <div className="robot-drawer-header">

                    <div>
                        <h3>{robot.robot_name || robot.robot_sn}</h3>
                        <p>SN: {robot.robot_sn} {robot.deployment_zone ? `· ${robot.deployment_zone}` : ""}</p>
                        <div className="drawer-live-status">
                            <StatusPill robot={robot} />
                            <BatteryBar pct={robot.battery_rate} />
                        </div>

                        {

                            robot.last_task_label &&

                            <p className="drawer-last-task">
                                Previous status: {robot.last_task_label}
                            </p>

                        }
                    </div>

                    <button className="drawer-close-btn" onClick={onClose}>
                        <FaTimes />
                    </button>

                </div>

                <div className="drawer-actions">

                    {perms.add &&
                    <button
                        className="action-btn action-charge"
                        onClick={handleSendToCharging}
                        disabled={sendingCharge}
                        title="Send this robot to its charging dock now"
                    >
                        <FaChargingStation className={sendingCharge ? "spin" : ""} />
                        {sendingCharge ? "Sending..." : "Send to charging"}
                    </button>
                    }

                    {perms.add &&
                    <button
                        className="action-btn action-reception"
                        onClick={handleSendToReception}
                        disabled={sendingNav}
                        title={`Send this robot to ${robot.deployment_zone ? "reception" : "its configured reception point"}`}
                    >
                        <FaWalking className={sendingNav ? "spin" : ""} />
                        {sendingNav ? "Sending..." : "Send to reception"}
                    </button>
                    }

                </div>

                {commandNote && (

                    <p className={`command-note ${commandNote.ok ? "ok" : "err"}`}>
                        {commandNote.ok ? <FaCheckCircle /> : <FaBan />} {commandNote.text}
                    </p>

                )}

                <div className="drawer-date-picker">

                    <label htmlFor="report-date">Report date</label>

                    <input
                        id="report-date"
                        type="date"
                        value={reportDate}
                        max={getTodayDateString()}
                        onChange={(e) => setReportDate(e.target.value)}
                    />

                </div>

                {loading && (
                    <p className="empty-note"><FaSyncAlt className="spin" /> Loading report...</p>
                )}

                {!loading && error && (
                    <p className="empty-note">{error}</p>
                )}

                {!loading && !error && report && (

                    <>

                        <div className="drawer-stat-grid">

                            <div className="drawer-stat">
                                <div className="value">
                                    {report.battery_start ?? "-"}% → {report.battery_end ?? "-"}%
                                </div>
                                <div className="label">Battery start → end</div>
                            </div>

                            <div className="drawer-stat">
                                <div className="value">{formatDuration(report.time_idle_seconds)}</div>
                                <div className="label">Total idle time</div>
                            </div>

                            <div className="drawer-stat">
                                <div className="value">{report.interaction_count}</div>
                                <div className="label">Passenger interactions</div>
                            </div>

                            <div className="drawer-stat">
                                <div className="value">{report.battery_min ?? "-"}%</div>
                                <div className="label">Lowest battery</div>
                            </div>

                            <div className="drawer-stat">
                                <div className="value">{formatDuration(report.time_charging_seconds)}</div>
                                <div className="label">Time charging</div>
                            </div>

                            <div className="drawer-stat">
                                <div className="value">{report.emergency_count}</div>
                                <div className="label">Emergency events</div>
                            </div>

                        </div>

                        <div className="drawer-section">

                            <h4><FaMapMarkerAlt /> Route (named waypoints visited)</h4>

                            {report.route.length === 0 && (
                                <p className="empty-note">No location data recorded for this day.</p>
                            )}

                            <div className="route-list">

                                {report.route.map((stop, idx) => (

                                    <div className="route-item" key={idx}>
                                        <span className="pos">{stop.pos_name}</span>
                                        <span className="time">
                                            {formatTime(stop.arrived_at)} – {stop.left_at ? formatTime(stop.left_at) : "now"}
                                            {" "}({formatDuration(stop.duration_seconds)})
                                        </span>
                                    </div>

                                ))}

                            </div>

                        </div>

                        <div className="drawer-section">

                            <h4>Idle periods</h4>

                            {report.idle_periods.length === 0 && (
                                <p className="empty-note">No idle periods recorded for this day.</p>
                            )}

                            <div className="idle-list">

                                {report.idle_periods.map((period, idx) => (

                                    <div className="idle-item" key={idx}>
                                        <span>{formatTime(period.started_at)} – {period.ended_at ? formatTime(period.ended_at) : "ongoing"}</span>
                                        <span className="time">{formatDuration(period.duration_seconds)}</span>
                                    </div>

                                ))}

                            </div>

                        </div>

                        <div className="drawer-section">

                            <h4>Passenger interaction log</h4>

                            {report.interactions.length === 0 && (
                                <p className="empty-note">No passenger interactions logged for this day.</p>
                            )}

                            <div className="interaction-list">

                                {report.interactions.map((item) => (

                                    <div
                                        className={`interaction-item ${item.was_emergency ? "emergency" : ""}`}
                                        key={item.id}
                                    >
                                        <div className="q">Q: {item.passenger_question}</div>
                                        <div className="a">A: {item.robot_answer || "-"}</div>
                                        <div className="meta">
                                            <span>{formatTime(item.created_at)}</span>
                                            <span>{item.was_emergency ? `⚠ ${item.emergency_type}` : (item.navigate_to || "")}</span>
                                        </div>
                                    </div>

                                ))}

                            </div>

                        </div>

                    </>

                )}

                <div className="drawer-section">

                    <h4>Recent commands</h4>

                    {commands.length === 0 && (
                        <p className="empty-note">No commands sent to this robot yet.</p>
                    )}

                    <div className="command-list">

                        {commands.map((cmd) => (

                            <div className={`command-item ${cmd.status}`} key={cmd.id}>

                                <span className="command-type">
                                    {cmd.command_type === "go_charging" ? <FaChargingStation /> : <FaWalking />}
                                    {" "}
                                    {cmd.command_type === "go_charging"
                                        ? "Sent to charging"
                                        : `Sent to ${cmd.target_pos_name}`}
                                </span>

                                <span className={`command-tag ${cmd.triggered_by}`}>
                                    {cmd.triggered_by === "auto" ? "auto (low battery)" : `manual · ${cmd.requested_by || "admin"}`}
                                </span>

                                <span className="command-time">{formatDateTime(cmd.created_at)}</span>

                                <span className={`command-status ${cmd.status}`}>
                                    {cmd.status === "sent" ? <FaCheckCircle /> : <FaBan />} {cmd.status}
                                </span>

                                <OutcomeBadge outcome={cmd.outcome} />

                                {(cmd.orion_msg_id || cmd.orion_req_id) && (

                                    <span className="command-orion-ids" title="Give these to OrionStar support if this command needs escalating">
                                        {cmd.orion_msg_id && `msg_id: ${cmd.orion_msg_id}`}
                                        {cmd.orion_msg_id && cmd.orion_req_id && "  ·  "}
                                        {cmd.orion_req_id && `req_id: ${cmd.orion_req_id}`}
                                    </span>

                                )}

                            </div>

                        ))}

                    </div>

                </div>

            </div>

        </div>

    );

}

// ==============================================================
// Main page
// ==============================================================

function RobotFleet() {

    // Sync-now and the quick-action charge/reception icons are all
    // POST calls, gated by require_menu_permission("robot_fleet").
    const perms = getMenuPermissions("robot_fleet");

    const [fleet, setFleet] = useState([]);

    const [summary, setSummary] = useState(null);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState(null);

    const [syncing, setSyncing] = useState(false);

    const [lastRefreshed, setLastRefreshed] = useState(null);

    const [selectedRobot, setSelectedRobot] = useState(null);

    const [quickActionBusy, setQuickActionBusy] = useState({}); // { [robot_sn]: "charge" | "nav" }

    const [quickActionNote, setQuickActionNote] = useState(null); // { robot_sn, ok, text }

    const intervalRef = useRef(null);

    const loadFleet = useCallback(async () => {

        setError(null);

        try {

            const [fleetRes, summaryRes] = await Promise.all([
                fetch(FLEET_ENDPOINT),
                fetch(SUMMARY_ENDPOINT)
            ]);

            if (!fleetRes.ok) {
                throw new Error(`Request failed with status ${fleetRes.status}`);
            }

            const fleetData = await fleetRes.json();

            setFleet(fleetData);

            if (summaryRes.ok) {

                setSummary(await summaryRes.json());

            }

            setLastRefreshed(new Date());

        } catch (err) {

            console.error("Fleet load failed:", err);

            setError(err.message || "Unable to reach the robot fleet API");

        } finally {

            setLoading(false);

        }

    }, []);

    const handleSync = useCallback(async () => {

        setSyncing(true);

        try {

            const res = await fetch(SYNC_ENDPOINT, { method: "POST" });

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            await loadFleet();

        } catch (err) {

            console.error("Manual sync failed:", err);

        } finally {

            setSyncing(false);

        }

    }, [loadFleet]);

    const handleQuickCommand = useCallback(async (robotSn, type) => {

        setQuickActionBusy((prev) => ({ ...prev, [robotSn]: type }));

        setQuickActionNote(null);

        const endpoint = type === "charge" ? goChargingEndpoint(robotSn) : navigateEndpoint(robotSn);

        try {

            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ requested_by: "admin-ui" })
            });

            if (!res.ok) {

                const body = await res.json().catch(() => ({}));

                throw new Error(body.detail || `Request failed with status ${res.status}`);

            }

            const data = await res.json();

            setQuickActionNote({
                robot_sn: robotSn,
                ok: true,
                text: type === "charge"
                    ? "Heading to charging dock."
                    : `Heading to ${data.target_pos_name}.`
            });

            await loadFleet();

        } catch (err) {

            console.error("Quick command failed:", err);

            setQuickActionNote({ robot_sn: robotSn, ok: false, text: err.message || "Command failed." });

        } finally {

            setQuickActionBusy((prev) => {
                const next = { ...prev };
                delete next[robotSn];
                return next;
            });

        }

    }, [loadFleet]);

    useEffect(() => {

        loadFleet();

        intervalRef.current = setInterval(loadFleet, AUTO_REFRESH_MS);

        return () => clearInterval(intervalRef.current);

    }, [loadFleet]);

    return (

        <div className="robot-fleet">

            <div className="robot-fleet-header">

                <div>
                    <h2><FaRobot /> Robot Fleet</h2>
                   {/*  <p className="robot-fleet-subtitle">
                        Live status from the OrionStar Robot OpenAPI, refreshed every {AUTO_REFRESH_MS / 1000}s.
                    </p> */}
                </div>

                <div className="robot-fleet-actions">

                    {lastRefreshed && (
                        <span className="last-synced">
                            Last refreshed: {formatDateTime(lastRefreshed)}
                        </span>
                    )}

                    {perms.add &&
                    <button className="sync-btn" onClick={handleSync} disabled={syncing}>
                        <FaSyncAlt className={syncing ? "spin" : ""} />
                        {syncing ? "Syncing..." : "Sync now"}
                    </button>
                    }

                </div>

            </div>

            {summary && (

                <div className="fleet-summary-grid">

                    <div className="summary-card">
                        <div className="summary-value">{summary.total_robots}</div>
                        <div className="summary-label">Total robots</div>
                    </div>

                    <div className="summary-card">
                        <div className="summary-value">{summary.online}</div>
                        <div className="summary-label">Online</div>
                    </div>

                    <div className="summary-card">
                        <div className="summary-value">{summary.offline}</div>
                        <div className="summary-label">Offline</div>
                    </div>

                    <div className="summary-card">
                        <div className="summary-value">{summary.idle}</div>
                        <div className="summary-label">Idle</div>
                    </div>

                    <div className="summary-card">
                        <div className="summary-value">{summary.charging}</div>
                        <div className="summary-label">Charging</div>
                    </div>

                    <div className="summary-card summary-alert">
                        <div className="summary-value">{summary.low_battery}</div>
                        <div className="summary-label">Low battery</div>
                    </div>

                </div>

            )}

            <div className="fleet-table-wrapper">

                <table className="fleet-table">

                    <thead>
                        <tr>
                            <th>Robot</th>
                            <th>Status</th>
                            <th>Battery</th>
                            <th>Deployment Zone</th>
                            <th style={{ display: "none" }}>Location</th>
                            <th>Last update</th>
                            <th>Quick actions</th>
                        </tr>
                    </thead>

                    <tbody>

                        {loading && (
                            <tr>
                                <td colSpan={6} className="fleet-table-message">
                                    <FaSyncAlt className="spin" /> Loading fleet...
                                </td>
                            </tr>
                        )}

                        {!loading && error && (
                            <tr>
                                <td colSpan={6} className="fleet-table-message">{error}</td>
                            </tr>
                        )}

                        {!loading && !error && fleet.length === 0 && (
                            <tr>
                                <td colSpan={6} className="fleet-table-message">
                                    No robots registered yet. Check ORION_APPID / ORION_OV_CORPID
                                    on the middleware, or click "Sync now".
                                </td>
                            </tr>
                        )}

                        {!loading && !error && fleet.map((robot) => (

                            <tr key={robot.robot_sn} onClick={() => setSelectedRobot(robot)}>

                                <td>
                                    <div className="robot-name-cell">
                                        <strong>{robot.robot_name || robot.robot_sn}</strong>
                                        <span>{robot.deployment_zone || robot.robot_sn}</span>
                                    </div>
                                </td>

                                <td><StatusPill robot={robot} /></td>

                                <td><BatteryBar pct={robot.battery_rate} /></td>
                                <td>{robot.deployment_zone || robot.robot_sn}</td>
                                <td style={{ display: "none" }}>{robot.pos_name || "-"}</td>

                                <td>{formatDateTime(robot.last_polled_at)}</td>

                                <td onClick={(e) => e.stopPropagation()}>

                                    <div className="quick-actions-cell">

                                        {perms.add &&
                                        <button
                                            className="quick-btn quick-charge"
                                            title="Send to charging"
                                            disabled={!!quickActionBusy[robot.robot_sn]}
                                            onClick={() => handleQuickCommand(robot.robot_sn, "charge")}
                                        >
                                            <FaChargingStation className={quickActionBusy[robot.robot_sn] === "charge" ? "spin" : ""} />
                                        </button>
                                        }

                                        {perms.add &&
                                        <button
                                            className="quick-btn quick-nav"
                                            title="Send to reception"
                                            disabled={!!quickActionBusy[robot.robot_sn]}
                                            onClick={() => handleQuickCommand(robot.robot_sn, "nav")}
                                        >
                                            <FaWalking className={quickActionBusy[robot.robot_sn] === "nav" ? "spin" : ""} />
                                        </button>
                                        }

                                        {quickActionNote && quickActionNote.robot_sn === robot.robot_sn && (

                                            <span className={`quick-note ${quickActionNote.ok ? "ok" : "err"}`}>
                                                {quickActionNote.ok ? <FaCheckCircle /> : <FaBan />}
                                            </span>

                                        )}

                                    </div>

                                </td>

                            </tr>

                        ))}

                    </tbody>

                </table>

            </div>

            {selectedRobot && (
                <RobotDrawer
                    robot={selectedRobot}
                    onClose={() => setSelectedRobot(null)}
                    onCommandSent={loadFleet}
                />
            )}

        </div>

    );

}

export default RobotFleet;
